import { useState } from 'react';
import { Button, Card, ConfirmDialog, EmptyState, IconMore, LoadingList, MenuButton, PageHeader } from '@timely/ui';
import { holds, type TemplateChannel } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { TemplateDialog } from '../templates/TemplateDialog';
import {
    archiveTemplate, deleteTemplate, duplicateTemplate, setDefaultTemplate, useTemplates,
    type MessageTemplate,
} from '../templates/useTemplates';

const CHANNEL_LABEL: Record<TemplateChannel, string> = {
    whatsapp: 'WhatsApp', email: 'Email', internal: 'Internal note',
};

/**
 * Message templates.
 *
 * Read by anyone on the team — using a template a manager wrote is not the
 * same right as writing one — and managed only by `templates.manage`, the
 * same capability the data source and company settings screens use for
 * anything that shapes what other people see or send.
 *
 * A brand-new company starts with none. That is deliberate: a default
 * WhatsApp reminder written for a different company's customers is not a
 * safer starting point than a blank page and a clear next action.
 */
export function Templates() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const canManage = holds(context, 'templates.manage');

    const { templates, loading, error, reload } = useTemplates(workspace.id);
    const [editing, setEditing] = useState<MessageTemplate | 'new' | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<MessageTemplate | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

    const active = templates.filter(t => !t.archivedAt);
    const archived = templates.filter(t => t.archivedAt);

    const act = async (label: string, run: () => Promise<void>) => {
        setNotice(null);
        try {
            await run();
            await reload();
            setNotice({ tone: 'ok', text: label });
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof Error ? err.message : 'That did not work.' });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Page width="standard">
            <PageHeader
                title="Message templates"
                subtitle="The wording your team sends, kept in one place and never crossing into another company."
                actions={canManage
                    ? <Button variant="primary" onClick={() => setEditing('new')}>New template</Button>
                    : undefined}
            />

            {notice && <div ><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading your templates" rows={4} />
                ) : active.length === 0 ? (
                    <EmptyState
                        title="No templates yet"
                        hint={canManage
                            ? 'Write the wording your team sends for a reminder, and everyone on the team can use it.'
                            : 'Once somebody sets one up, it appears here.'}
                        action={canManage
                            ? <Button variant="primary" onClick={() => setEditing('new')}>Write your first template</Button>
                            : undefined}
                    />
                ) : (
                    <ul className="divide-y divide-separator">
                        {active.map(t => (
                            <li key={t.id} className="px-5 py-3.5 flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    {/* The channel and "default" are words beside the name, not two pills on every row. */}
                                    <p className="flex items-baseline gap-x-2 flex-wrap">
                                        <span className="font-semibold text-label">{t.name}</span>
                                        <span className="text-secondary text-label-3">
                                            {CHANNEL_LABEL[t.channel]}{t.isDefault && <> · <span className="text-accent-text font-semibold">Default</span></>}
                                        </span>
                                    </p>
                                    {/* The opening words, not the greeting's blank line: every starter template begins "Hello,". */}
                                    <p className="text-secondary text-label-3 mt-1 line-clamp-2">{t.content.replace(/\s*\n\s*/g, ' ')}</p>
                                </div>
                                {canManage && (
                                    // The everyday action, and the rest behind one "More": four outlined buttons of four
                                    // different widths on every row never lined up (2026-10 global sweep).
                                    <div className="flex items-center gap-0.5 flex-none">
                                        <Button variant="row" size="sm" onClick={() => setEditing(t)}>Edit</Button>
                                        <MenuButton
                                            label={<IconMore size={18} />}
                                            name={`More for ${t.name}`}
                                            variant="quiet"
                                            size="sm"
                                            buttonClassName="!w-8 !px-0"
                                            items={[
                                                { label: 'Duplicate', disabled: busyId === t.id, onSelect: () => { setBusyId(t.id); void act('Duplicated.', async () => { await duplicateTemplate(workspace.id, t); }); } },
                                                ...(t.isDefault ? [] : [{ label: 'Make default', disabled: busyId === t.id, onSelect: () => { setBusyId(t.id); void act('Set as default.', async () => { await setDefaultTemplate(workspace.id, t.id); }); } }]),
                                                { label: 'Archive', disabled: busyId === t.id, onSelect: () => { setBusyId(t.id); void act('Archived.', async () => { await archiveTemplate(t.id, true); }); } },
                                            ]}
                                        />
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {canManage && archived.length > 0 && (
                <Card className="mt-5 overflow-hidden">
                    <p className="px-5 pt-4 pb-1 text-secondary font-semibold text-label-3">Archived — kept, not offered</p>
                    <ul className="divide-y divide-separator">
                        {archived.map(t => (
                            <li key={t.id} className="px-5 py-3 flex items-center justify-between gap-3">
                                <p className="min-w-0 flex items-baseline gap-x-2 flex-wrap">
                                    <span className="font-semibold text-label-2">{t.name}</span>
                                    <span className="text-secondary text-label-3">{CHANNEL_LABEL[t.channel]}</span>
                                </p>
                                <div className="flex items-center gap-0.5 shrink-0">
                                    <Button
                                        variant="row" size="sm" disabled={busyId === t.id}
                                        onClick={() => { setBusyId(t.id); void act('Restored.', async () => { await archiveTemplate(t.id, false); }); }}
                                    >Restore</Button>
                                    <Button
                                        variant="quiet" size="sm" className="hover:!text-dang" disabled={busyId === t.id}
                                        onClick={() => setConfirmDelete(t)}
                                    >Delete</Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {editing && (
                <TemplateDialog
                    workspaceId={workspace.id}
                    template={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async message => {
                        setEditing(null);
                        await reload();
                        setNotice({ tone: 'ok', text: message });
                    }}
                />
            )}

            {confirmDelete && (
                <ConfirmDialog
                    open
                    title="Delete this template?"
                    confirmLabel="Delete"
                    tone="danger"
                    onCancel={() => setConfirmDelete(null)}
                    onConfirm={async () => {
                        const t = confirmDelete;
                        setConfirmDelete(null);
                        await act('Deleted.', async () => { await deleteTemplate(t.id); });
                    }}
                >
                    "{confirmDelete.name}" will be gone for good. Nothing else refers to it.
                </ConfirmDialog>
            )}
        </Page>
    );
}
